import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI } from '../App';
import { useTheme } from '../ThemeContext';
import { getPalette } from '../themePalette';
import ProjectCard from './ProjectCard';

const API_URL = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin) + '/api';

const ProjectList = ({ onSelectProject, isOffline }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const T = getPalette(theme);
  const [showImportPicker, setShowImportPicker] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [stats, setStats] = useState({});
  const [coverBusy, setCoverBusy] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    loadProjects();
  }, [showInactive]);

  // Uses the LIGHTWEIGHT summary endpoint (id/name/client/cover/order + room
  // count) instead of GET /projects which nests every room+item (~70MB) and was
  // why this page "didn't work half the time".
  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectAPI.getSummary(showInactive ? { only_inactive: true } : undefined);
      const list = (response.data || []).map((p) => ({
        ...p,
        clientName: p.client_info?.full_name || 'Unknown Client',
        address: p.client_info?.address || '',
      }));
      setProjects(list);
      setError(null);
      list.forEach((p) => loadStats(p.id));
    } catch (err) {
      setError('Failed to load projects');
      console.error('Error loading projects:', err);
      const cachedProjects = localStorage.getItem('cached_projects');
      if (cachedProjects) {
        setProjects(JSON.parse(cachedProjects));
        setError('Using cached data - some changes may not be saved');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (projectId) => {
    try {
      const res = await fetch(`${API_URL}/dashboard/progress/${projectId}`);
      const d = await res.json();
      if (d && d.success) setStats((prev) => ({ ...prev, [projectId]: d }));
    } catch (e) { /* non-critical */ }
  };

  const toggleActive = async (project, e) => {
    if (e) e.stopPropagation();
    const makeInactive = project.active !== false;
    if (!window.confirm(`${makeInactive ? 'Close out' : 'Reopen'} "${project.name}"?\n\nClosed-out clients are hidden from the dashboard and the main project list, but nothing is deleted — you can bring them back any time.`)) return;
    try {
      await projectAPI.update(project.id, { active: !makeInactive });
      loadProjects();
    } catch (error) {
      alert('❌ Failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Change a file's status inline from the card — kept identical to the dashboard.
  const changeStatus = async (project, newStatus) => {
    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, status: newStatus } : p)));
    try {
      await projectAPI.update(project.id, { status: newStatus });
    } catch (error) {
      alert('❌ Failed to update status: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Archive (or reactivate) several client files at once.
  const bulkToggleActive = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const makeInactive = !showInactive; // active view -> close out; inactive view -> reactivate
    const verb = makeInactive ? 'Close out (make inactive)' : 'Reactivate';
    if (!window.confirm(`${verb} ${ids.length} client${ids.length > 1 ? 's' : ''}?\n\nNothing is deleted — you can bring them back any time.`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((id) => projectAPI.update(id, { active: !makeInactive })));
      setSelected(new Set());
      await loadProjects();
    } catch (error) {
      alert('❌ Bulk update failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDeleteProject = async (project, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${project.name}"?\n\nThis will delete ALL rooms, items, and data. This cannot be undone!`)) return;
    try {
      await projectAPI.delete(project.id);
      loadProjects();
    } catch (error) {
      alert('❌ Failed to delete project: ' + error.message);
    }
  };

  const uploadCover = async (project, e) => {
    const fileEl = e.target;
    const file = fileEl.files && fileEl.files[0];
    if (!file) return;
    setCoverBusy(project.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/projects/${project.id}/cover`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Cover upload failed');
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, cover_image: data.cover_image } : p)));
    } catch (err) {
      alert('Cover upload failed: ' + (err.message || err));
    } finally {
      setCoverBusy(null);
      fileEl.value = '';
    }
  };

  // Set the cover to a photo already in the project (no upload) — chosen in CoverPicker.
  const setCoverUrl = async (project, url) => {
    setCoverBusy(project.id);
    try {
      await projectAPI.update(project.id, { cover_image: url });
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, cover_image: url } : p)));
    } catch (err) {
      alert('Failed to set cover: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCoverBusy(null);
    }
  };

  const createSampleProject = async () => {
    const sampleProject = {
      name: 'Greene Renovation',
      client_info: { full_name: 'Emileigh Greene', email: 'emileigh.greene@goldcreekfoods.com', phone: '6782305388', address: '4567 Crooked Creek Road, Gainesville, Georgia, 30506' },
      project_type: 'Renovation', timeline: 'NOW', budget: '600k-1M',
      style_preferences: ['Transitional', 'Traditional'], color_palette: 'Neutral with pops of color', special_requirements: 'Pet-friendly materials',
    };
    try {
      await projectAPI.create(sampleProject);
      await loadProjects();
    } catch (err) {
      setError('Failed to create sample project');
    }
  };

  const btn = (active) => `px-5 py-2.5 rounded-lg text-sm font-semibold transition-all`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64" style={{ color: T.muted }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: T.gold }}></div>
          <p className="mt-4">Loading projects…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="-mx-4 -my-6 min-h-screen px-4 py-6 sm:px-6" style={{ background: T.page, color: T.text, fontFamily: "'Jost','Century Gothic',sans-serif" }} data-testid="projects-view-all">
      <div className="mx-auto max-w-[1700px]">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: T.text }}>{showInactive ? 'Inactive Clients' : 'All Projects'}</h1>
            <p className="text-sm" style={{ color: T.text2 }}>{showInactive ? 'Archived clients — reactivate any time' : 'Every active client file'} <span style={{ color: T.muted }}>({projects.length})</span></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="toggle-inactive-view" onClick={() => { setSelected(new Set()); setShowInactive(v => !v); }} className={btn()} style={{ background: showInactive ? T.goldGrad : T.chip, color: showInactive ? T.onGold : T.text, border: `1px solid ${T.border}` }}>
              {showInactive ? '← Back to Active' : '💤 Show Inactive'}
            </button>
            <button data-testid="create-sample-project" onClick={createSampleProject} className={btn()} style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>🏠 Create Sample</button>
            <button data-testid="import-from-sheets" onClick={() => setShowImportPicker(true)} disabled={projects.length === 0} title={projects.length === 0 ? 'Create a project first' : 'Choose which project to import into'} className={`${btn()} disabled:opacity-40`} style={{ background: T.goldGrad, color: T.onGold }}>📥 Import from Sheets</button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg p-4" style={{ background: `${T.danger}18`, border: `1px solid ${T.danger}`, color: T.danger }}>{error}</div>
        )}

        {selected.size > 0 && (
          <div data-testid="bulk-action-bar" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3" style={{ background: T.card, border: `1px solid ${T.borderStrong}` }}>
            <span className="text-sm font-semibold" style={{ color: T.text }}>{selected.size} selected</span>
            <div className="flex gap-2">
              <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} className="rounded px-3 py-1.5 text-xs font-semibold" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>Clear</button>
              <button data-testid="bulk-archive-btn" disabled={bulkBusy} onClick={bulkToggleActive} className="rounded px-3 py-1.5 text-xs font-bold disabled:opacity-60" style={{ background: T.goldGrad, color: T.onGold }}>
                {bulkBusy ? 'Working…' : (showInactive ? `♻ Reactivate ${selected.size}` : `🗄 Close out ${selected.size}`)}
              </button>
            </div>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: T.card, border: `1px solid ${T.border}` }}>
            <div className="mb-3 text-5xl">🏗️</div>
            <h3 className="mb-1 text-xl font-semibold" style={{ color: T.text }}>{showInactive ? 'No inactive clients' : 'No Projects Yet'}</h3>
            <p className="mb-6 text-sm" style={{ color: T.text2 }}>{showInactive ? 'Everything is active.' : 'Get started by creating your first project.'}</p>
            {!showInactive && (
              <button onClick={createSampleProject} className="rounded-lg px-8 py-3 text-sm font-bold" style={{ background: T.goldGrad, color: T.onGold }}>Create Your First Project</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {projects.map((project) => {
              const stat = stats[project.id];
              const pct = stat ? Math.round(stat.completion_percentage || 0) : null;
              const items = stat ? stat.total_items : (project.room_count != null ? project.room_count : null);
              return (
                <div key={project.id} className="relative">
                  <button
                    data-testid={`select-project-${project.id}`}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(project.id); }}
                    title="Select for bulk close-out"
                    className="absolute left-3 top-3 z-20 grid h-6 w-6 place-items-center rounded-md text-[12px] font-bold transition-all"
                    style={selected.has(project.id)
                      ? { background: T.goldGrad, color: T.onGold, border: `1px solid ${T.gold}` }
                      : { background: 'rgba(0,0,0,0.55)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)' }}
                  >
                    {selected.has(project.id) ? '✓' : ''}
                  </button>
                  <ProjectCard
                    project={project}
                    theme={theme}
                    items={stat ? stat.total_items : null}
                    pct={pct}
                    onDelete={handleDeleteProject}
                    onCoverUpload={uploadCover}
                    onCoverSet={setCoverUrl}
                    coverBusy={coverBusy === project.id}
                    onStatusChange={changeStatus}
                    onCloseOut={(p) => toggleActive(p)}
                    onReopen={(p) => toggleActive(p)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {showImportPicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
            <div className="w-full max-w-md rounded-2xl p-6" style={{ background: T.card, border: `1px solid ${T.borderStrong}` }}>
              <h3 className="mb-1 text-lg font-bold" style={{ color: T.gold }}>📥 Import from Sheets</h3>
              <p className="mb-4 text-xs" style={{ color: T.text2 }}>Which project should the sheet be imported into?</p>
              <div className="mb-4 max-h-72 space-y-2 overflow-y-auto">
                {projects.map((p) => (
                  <button key={p.id} data-testid={`import-target-${p.id}`} onClick={() => { setShowImportPicker(false); navigate(`/project/${p.id}?tab=Trade Sheets`); }} className="w-full rounded px-3 py-2 text-left text-sm" style={{ background: T.cardSubtle, border: `1px solid ${T.border}`, color: T.text }}>
                    {p.name}{p.clientName ? <span style={{ color: T.muted }} className="text-xs"> — {p.clientName}</span> : null}
                  </button>
                ))}
              </div>
              <button onClick={() => setShowImportPicker(false)} className="w-full rounded px-4 py-2 text-sm font-bold" style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectList;
